import type { CatalogSort } from '@xshop/shared';
import { useElementRender } from './elementRenderContext';

// The words a listing says to a shopper, in each language a store may be served in. They are the shop's
// furniture rather than its content — "Sort by", "In stock", "Remove all" — so a merchant does not type
// them, and a French or Arabic shop does not greet its shoppers in English.

/** Everything the filters, the toolbar and the pagination say. */
export type ListingWords = {
  filter: string; filters: string; availability: string; inStock: string; price: string; from: string; to: string;
  type: string; vendor: string; sortBy: string; removeAll: string; remove: string; close: string; show: (count: number) => string;
  products: (count: number) => string; pages: string; previous: string; next: string; page: (at: number) => string;
  noMatch: string; empty: string; sorts: Record<CatalogSort, string>;
  results: (count: number) => string; noResults: (words: string) => string; searchHint: string; clear: string; copied: string; pictures: string; previousPicture: string; nextPicture: string; openPicture: (at: number, of: number) => string;
};

const EN: ListingWords = {
  filter: 'Filter', filters: 'Filters', availability: 'Availability', inStock: 'In stock', price: 'Price', from: 'From', to: 'To',
  type: 'Product type', vendor: 'Brand', sortBy: 'Sort by', removeAll: 'Remove all', remove: 'Remove', close: 'Close',
  show: (count) => `Show ${count} ${count === 1 ? 'product' : 'products'}`,
  products: (count) => `${count} ${count === 1 ? 'product' : 'products'}`,
  pages: 'Pages', previous: 'Previous page', next: 'Next page', page: (at) => `Page ${at}`,
  noMatch: 'No products match these filters.', empty: 'There are no products here yet.',
  results: (count) => `${count} ${count === 1 ? 'result' : 'results'}`, noResults: (words) => `No results found for “${words}”.`,
  searchHint: 'Check the spelling or use a different word or phrase.', clear: 'Clear', copied: 'Link copied', pictures: 'Pictures', previousPicture: 'Previous picture', nextPicture: 'Next picture',
  openPicture: (at, of) => `Open picture ${at} of ${of}`,
  sorts: {
    manual: 'Featured', 'best-selling': 'Best selling', 'price-asc': 'Price, low to high', 'price-desc': 'Price, high to low',
    newest: 'Newest', 'title-asc': 'Alphabetically, A-Z', 'title-desc': 'Alphabetically, Z-A', relevance: 'Relevance'
  }
};

const FR: ListingWords = {
  filter: 'Filtrer', filters: 'Filtres', availability: 'Disponibilité', inStock: 'En stock', price: 'Prix', from: 'De', to: 'À',
  type: 'Type de produit', vendor: 'Marque', sortBy: 'Trier par', removeAll: 'Tout effacer', remove: 'Retirer', close: 'Fermer',
  show: (count) => `Voir ${count} ${count === 1 ? 'produit' : 'produits'}`,
  products: (count) => `${count} ${count === 1 ? 'produit' : 'produits'}`,
  pages: 'Pages', previous: 'Page précédente', next: 'Page suivante', page: (at) => `Page ${at}`,
  noMatch: 'Aucun produit ne correspond à ces filtres.', empty: 'Il n’y a pas encore de produits ici.',
  results: (count) => `${count} ${count === 1 ? 'résultat' : 'résultats'}`, noResults: (words) => `Aucun résultat pour « ${words} ».`,
  searchHint: 'Vérifiez l’orthographe ou essayez un autre mot.', clear: 'Effacer', copied: 'Lien copié', pictures: 'Photos', previousPicture: 'Photo précédente', nextPicture: 'Photo suivante',
  openPicture: (at, of) => `Agrandir la photo ${at} sur ${of}`,
  sorts: {
    manual: 'En vedette', 'best-selling': 'Meilleures ventes', 'price-asc': 'Prix croissant', 'price-desc': 'Prix décroissant',
    newest: 'Nouveautés', 'title-asc': 'De A à Z', 'title-desc': 'De Z à A', relevance: 'Pertinence'
  }
};

const AR: ListingWords = {
  filter: 'تصفية', filters: 'عوامل التصفية', availability: 'التوفر', inStock: 'متوفر', price: 'السعر', from: 'من', to: 'إلى',
  type: 'نوع المنتج', vendor: 'العلامة', sortBy: 'ترتيب حسب', removeAll: 'مسح الكل', remove: 'إزالة', close: 'إغلاق',
  show: (count) => `عرض ${count} منتج`,
  products: (count) => `${count} منتج`,
  pages: 'الصفحات', previous: 'الصفحة السابقة', next: 'الصفحة التالية', page: (at) => `الصفحة ${at}`,
  noMatch: 'لا توجد منتجات تطابق هذه الاختيارات.', empty: 'لا توجد منتجات هنا بعد.',
  results: (count) => `${count} نتيجة`, noResults: (words) => `لا توجد نتائج لـ «${words}».`,
  searchHint: 'تحقق من الإملاء أو جرّب كلمة أخرى.', clear: 'مسح', copied: 'تم نسخ الرابط', pictures: 'الصور', previousPicture: 'الصورة السابقة', nextPicture: 'الصورة التالية',
  openPicture: (at, of) => `تكبير الصورة ${at} من ${of}`,
  sorts: {
    manual: 'مميز', 'best-selling': 'الأكثر مبيعًا', 'price-asc': 'السعر من الأقل', 'price-desc': 'السعر من الأعلى',
    newest: 'الأحدث', 'title-asc': 'أبجديًا، أ-ي', 'title-desc': 'أبجديًا، ي-أ', relevance: 'الأكثر صلة'
  }
};

const WORDS: Readonly<Record<'en' | 'fr' | 'ar', ListingWords>> = { en: EN, fr: FR, ar: AR };

// Gives the listing's words in the store's language.
export function useListingWords(): ListingWords {
  const { store } = useElementRender();
  return WORDS[store?.language ?? 'en'];
}
